// 2026-11-02: Admin > People > an account > Spend (/admin/people/:id). What the account cost us (provider dollars)
// against what it was charged (credits), over a window: the totals, a daily chart, where it went by kind of work, by
// video and by model, the Director's share, and the latest credit lines. GET /api/admin/people/:id/spend?window=.
import { useState } from "react";
import { call } from "../api";
import { Empty, ErrorNote, fmtCredits, Spinner, timeAgo, useLoad } from "../components/bits";
import { Shell } from "../components/Shell";
import { Link } from "../router";
import { chartTop, KIND_LABEL, kindScale, markupText, normPersonSpend, sortKinds, SPEND_WINDOWS, usd, type PersonSpend, type SpendWindow } from "../personspend";
import { useStore } from "../store";
import { isLow, LOW_MARKUP } from "./AdminCosts";

type Person = { id: string; name: string; email: string; balance: number };

// the mock app (VITE_MOCK=1) reads made-up numbers (mock/personspend.ts); the import is dropped from the real build
const MOCK = import.meta.env.VITE_MOCK === "1";

async function load(id: string, w: SpendWindow): Promise<{ person: Person; spend: PersonSpend }> {
  const r = (MOCK ? (await import("../mock/personspend")).mockPersonSpend(id, w) : await call("GET", `admin/people/${encodeURIComponent(id)}/spend?window=${w}`)) as { person?: Person; spend?: unknown };
  return { person: r.person as Person, spend: normPersonSpend(r.spend) };
}

export function PersonSpendScreen({ id }: { id: string }) {
  const me = useStore((s) => s.me);
  const [win, setWin] = useState<SpendWindow>("30d");
  const { data, error, loading, reload } = useLoad(() => load(id, win), [id, win]);
  if (me && me.user.role !== "admin")
    return (
      <Shell>
        <div className="page">
          <Empty title="Admins only" action={<Link to="/" className="btn btn-quiet">Go home</Link>}>
            This page shows what an account costs to run.
          </Empty>
        </div>
      </Shell>
    );
  const s = data?.spend;
  const p = data?.person;
  return (
    <Shell>
      <div className="page costs pspend">
        <Link to="/admin/people" className="back">
          People
        </Link>
        <header className="page-head costs-head">
          <div>
            <h1 className="page-title">{p ? p.name || p.email : "Spend"}</h1>
            <p className="page-lede">{p ? `${p.email} · what this account cost us against what it was charged` : "Loading the account"}</p>
          </div>
          <span className="seg" role="radiogroup" aria-label="Window">
            {SPEND_WINDOWS.map((w) => (
              <button key={w.id} type="button" role="radio" aria-checked={w.id === win} className={w.id === win ? "is-on" : ""} onClick={() => setWin(w.id)}>
                {w.label}
              </button>
            ))}
          </span>
        </header>
        {error && <ErrorNote text={`Couldn't load the spend. ${error}`} retry={reload} />}
        {loading && !data && <Spinner label="Adding it up" />}
        {s && (
          <div className={loading ? "is-dim" : ""}>
            <dl className="costs-big">
              <div>
                <dt>Cost us</dt>
                <dd>{usd(s.totals.costUsd)}</dd>
                <p className="costs-markup">provider spend</p>
              </div>
              <div>
                <dt>Charged</dt>
                <dd>{usd(s.totals.chargedUsd)}</dd>
                <p className="costs-markup">{fmtCredits(s.totals.chargedCredits)} credits</p>
              </div>
              <div className={isLow(s.totals.markup, s.totals.costUsd) ? "is-low" : ""}>
                <dt>Margin</dt>
                <dd>{usd(s.totals.marginUsd)}</dd>
                <p className="costs-markup">{markupText(s.totals.markup)} markup</p>
              </div>
            </dl>
            <ul className="pspend-facts" aria-label="Credits">
              <li>
                <span>Balance</span>
                <strong>{fmtCredits(s.totals.balance)}</strong>
              </li>
              <li>
                <span>Bought</span>
                <strong>{fmtCredits(s.totals.boughtCredits)}</strong>
                <em>{s.totals.purchases ? `${s.totals.purchases} purchase${s.totals.purchases === 1 ? "" : "s"}` : "none"}</em>
              </li>
              <li>
                <span>Given</span>
                <strong>{fmtCredits(s.totals.givenCredits)}</strong>
                <em>invites and grants</em>
              </li>
              <li>
                <span>Director</span>
                <strong>{s.director.minutes.toLocaleString("en-US", { maximumFractionDigits: 1 })} min</strong>
                <em>
                  {s.director.calls} call{s.director.calls === 1 ? "" : "s"} · {usd(s.director.costUsd)} cost · {fmtCredits(s.director.chargedCredits)} credits
                </em>
              </li>
            </ul>

            <section className="costs-sec" aria-labelledby="ps-day-h">
              <h2 id="ps-day-h" className="group-title">
                By day
              </h2>
              <DailyChart days={s.daily} />
            </section>

            <section className="costs-sec" aria-labelledby="ps-kind-h">
              <h2 id="ps-kind-h" className="group-title">
                Where it went
              </h2>
              {s.byKind.length ? <KindBars rows={s.byKind} /> : <p className="muted">Nothing made or charged in this window.</p>}
              <p className="muted costs-note">Red rows earn under {LOW_MARKUP}× what they cost. A refund (a failed job) takes its credits back from the same kind.</p>
            </section>

            <div className="costs-split">
              <section className="costs-sec" aria-labelledby="ps-proj-h">
                <h2 id="ps-proj-h" className="group-title">
                  By video
                </h2>
                {s.byProject.length ? (
                  <div className="table-wrap">
                    <table className="costs-table">
                      <thead>
                        <tr>
                          <th scope="col">Video</th>
                          <th scope="col" className="num">
                            Cost
                          </th>
                          <th scope="col" className="num">
                            Credits
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.byProject.map((r) => (
                          <tr key={r.projectId ?? r.title}>
                            <th scope="row">{r.projectId ? <Link to={`/p/${r.projectId}`}>{r.title}</Link> : <span className="muted">{r.title}</span>}</th>
                            <td className="num">{usd(r.costUsd)}</td>
                            <td className="num">{fmtCredits(r.chargedCredits)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="muted">No videos in this window.</p>
                )}
              </section>
              <section className="costs-sec" aria-labelledby="ps-model-h">
                <h2 id="ps-model-h" className="group-title">
                  By model
                </h2>
                {s.byModel.length ? (
                  <div className="table-wrap">
                    <table className="costs-table">
                      <thead>
                        <tr>
                          <th scope="col">Model</th>
                          <th scope="col" className="num">
                            Calls
                          </th>
                          <th scope="col" className="num">
                            Cost
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.byModel.map((m) => (
                          <tr key={`${m.provider}|${m.model}`}>
                            <th scope="row">
                              <span className="pspend-model">{m.model}</span>
                              <span className="muted pspend-sub">
                                {m.provider} · {KIND_LABEL[m.kind].toLowerCase()}
                              </span>
                            </th>
                            <td className="num">
                              {m.calls}
                              {m.failed ? <span className="tag tag-warn pspend-failed">{m.failed} failed</span> : null}
                            </td>
                            <td className="num">{usd(m.costUsd)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="muted">No provider calls in this window.</p>
                )}
              </section>
            </div>

            <section className="costs-sec" aria-labelledby="ps-recent-h">
              <h2 id="ps-recent-h" className="group-title">
                Latest credit lines
              </h2>
              {s.recent.length ? (
                <ul className="pspend-recent">
                  {s.recent.map((r, i) => (
                    <li key={`${r.at}-${i}`}>
                      <span className={`ppl-delta${r.credits > 0 ? " is-in" : ""}`}>
                        {r.credits > 0 ? "+" : "−"}
                        {fmtCredits(Math.abs(r.credits))}
                      </span>
                      <span className="pspend-what">
                        <span>{r.label}</span>
                        {r.project && <span className="muted"> · {r.project}</span>}
                      </span>
                      <span className="num pspend-cost">{r.costUsd != null ? `cost ${usd(r.costUsd)}` : ""}</span>
                      <span className="muted pspend-when">{timeAgo(r.at)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">No credit lines in this window.</p>
              )}
            </section>
          </div>
        )}
      </div>
    </Shell>
  );
}

function KindBars({ rows }: { rows: PersonSpend["byKind"] }) {
  const scale = kindScale(rows);
  return (
    <ul className="pspend-kinds">
      {sortKinds(rows).map((r) => (
        <li key={r.kind} className={isLow(r.markup, r.costUsd) ? "is-low" : ""}>
          <span className="pspend-kind">
            {KIND_LABEL[r.kind]}
            <span className="muted pspend-sub">{r.calls ? `${r.calls} call${r.calls === 1 ? "" : "s"}` : "no provider calls"}</span>
          </span>
          <span className="pspend-bars" aria-hidden>
            <span className="pspend-bar is-charged" style={{ width: `${(Math.max(0, r.chargedUsd) / scale) * 100}%` }} />
            <span className="pspend-bar is-cost" style={{ width: `${(r.costUsd / scale) * 100}%` }} />
          </span>
          <span className="pspend-nums">
            <span>
              {usd(r.chargedUsd)} <span className="muted">({fmtCredits(r.chargedCredits)} cr)</span>
            </span>
            <span className="pspend-costnum">cost {usd(r.costUsd)}</span>
            <span className="pspend-x">{markupText(r.markup)}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function DailyChart({ days }: { days: PersonSpend["daily"] }) {
  const top = chartTop(days);
  const W = 720;
  const H = 140;
  const slot = W / Math.max(1, days.length);
  const bw = Math.max(2, Math.min(18, slot * 0.7));
  const y = (v: number) => H - (Math.max(0, v) / top) * H;
  const label = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const any = days.some((d) => d.costUsd > 0 || d.chargedUsd > 0);
  return (
    <figure className="pspend-chart">
      <div className="pspend-legend" aria-hidden>
        <span className="is-charged">Charged</span>
        <span className="is-cost">Cost us</span>
        <span className="muted">top of chart {usd(top)}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={any ? "What this account was charged and cost us each day" : "No spend in this window"}>
        <line x1="0" x2={W} y1={H - 0.5} y2={H - 0.5} className="pspend-axis" />
        <line x1="0" x2={W} y1={H / 2} y2={H / 2} className="pspend-grid" />
        {days.map((d, i) => {
          const x = i * slot + (slot - bw) / 2;
          return (
            <g key={d.day}>
              <title>{`${label(d.day)}: charged ${usd(d.chargedUsd)}, cost us ${usd(d.costUsd)}`}</title>
              <rect x={i * slot} y={0} width={slot} height={H} className="pspend-hit" />
              {d.chargedUsd > 0 && <rect x={x} y={y(d.chargedUsd)} width={bw} height={H - y(d.chargedUsd)} rx={1.5} className="pspend-bar-charged" />}
              {d.costUsd > 0 && <rect x={x + bw * 0.25} y={y(d.costUsd)} width={bw * 0.5} height={H - y(d.costUsd)} rx={1} className="pspend-bar-cost" />}
            </g>
          );
        })}
      </svg>
      <figcaption className="pspend-dates">
        <span>{days[0] ? label(days[0].day) : ""}</span>
        <span>{days.at(-1) ? label(days.at(-1)!.day) : ""}</span>
      </figcaption>
    </figure>
  );
}
